import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, LOGO_DARK } from '../config';
import { setSeo } from '../lib/seo';

const SITE = 'https://app.dailydiscipline.app';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch (e) { return ''; }
};

const PublicNav = () => (
  <header className="border-b border-neutral-200 bg-white/90 backdrop-blur sticky top-0 z-30">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2" data-testid="blog-home-link">
        <img src={LOGO_DARK} alt="Daily Discipline" className="h-9 w-9" />
        <span className="font-bold text-neutral-900">Daily Discipline™</span>
      </Link>
      <Link to="/" className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-full px-5 py-2 transition" data-testid="blog-open-app">
        Open App →
      </Link>
    </div>
  </header>
);

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSeo({
      title: 'The Daily Discipline Blog — Habits, Identity & Self-Discipline',
      description: 'Honest, no-guilt writing on building habits, identity-based change, and lasting self-discipline — by Pam Matambanadzo, author of A Journey to Self-Discipline.',
      canonical: `${SITE}/blog`,
      url: `${SITE}/blog`,
      image: `${SITE}/og-image.png`,
      type: 'website',
      jsonld: {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'The Daily Discipline Blog',
        url: `${SITE}/blog`,
        author: { '@type': 'Person', name: 'Pam Matambanadzo' },
      },
    });
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/posts`);
        setPosts(res.data.posts || []);
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PublicNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="blog-list-page">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">The Journal</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-neutral-900 mt-2">
            Build discipline, without the guilt.
          </h1>
          <p className="text-lg text-neutral-600 mt-4 max-w-2xl">
            Honest writing on habits, identity, and showing up — from the team behind Daily Discipline
            and the book <em>A Journey to Self-Discipline</em>.
          </p>
        </div>

        {loading ? (
          <div className="py-20 text-center text-neutral-400">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="py-20 text-center text-neutral-400">No posts yet — check back soon. 🌱</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                {p.cover_image && (
                  <div className="h-48 overflow-hidden">
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6 flex flex-col gap-3 flex-1">
                  {p.tags?.length > 0 && (
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">{p.tags[0]}</span>
                  )}
                  <h2 className="text-xl font-bold text-neutral-900 leading-snug group-hover:text-emerald-700 transition-colors">{p.title}</h2>
                  <p className="text-neutral-600 text-sm flex-1 line-clamp-3">{p.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-neutral-400 pt-2">
                    <span>{p.author}</span>
                    <span>{fmtDate(p.published_at)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-neutral-200 mt-12 py-8 text-center text-sm text-neutral-400">
        © {new Date().getFullYear()} Daily Discipline™ ·{' '}
        <Link to="/legal/terms" className="hover:text-neutral-600">Terms</Link> ·{' '}
        <Link to="/legal/privacy" className="hover:text-neutral-600">Privacy</Link>
      </footer>
    </div>
  );
};

export default BlogList;
